import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckCircle2Icon, CheckIcon, LockIcon, CreditCardIcon, SparklesIcon, ArrowLeftIcon, Loader2Icon, TagIcon, AlertCircleIcon } from 'lucide-react';
// Demo referral codes → discount percentage off the monthly price.
const REFERRAL_CODES: Record<string, number> = {
  TALAB10: 10,
  WELCOME20: 20,
  EASYLEASE15: 15
};
type ReferralStatus = 'idle' | 'checking' | 'valid' | 'invalid';
import { useBusiness } from './BusinessContext';
type Step = 'choose' | 'pay' | 'success';
interface UpgradePlanModalProps {
  onClose: () => void;
  initialPlan?: string;
}
export function UpgradePlanModal({
  onClose,
  initialPlan
}: UpgradePlanModalProps) {
  const {
    trialPlan,
    subscribePackage,
    PACKAGES,
    currentPlanId
  } = useBusiness();
  const [step, setStep] = useState<Step>('choose');
  const [selected, setSelected] = useState<string>(initialPlan ?? trialPlan ?? 'Growth');
  const [processing, setProcessing] = useState(false);
  const [referral, setReferral] = useState('');
  const [referralStatus, setReferralStatus] = useState<ReferralStatus>('idle');
  const [discountPct, setDiscountPct] = useState(0);
  const paidPackages = PACKAGES.filter(p => p.id !== 'Free');
  const plan = PACKAGES.find(p => p.id === selected) || paidPackages[0];
  const discountAmount = Math.round(plan.price * discountPct / 100);
  const total = plan.price - discountAmount;
  const applyReferral = () => {
    const code = referral.trim().toUpperCase();
    if (!code) return;
    setReferralStatus('checking');
    setTimeout(() => {
      const pct = REFERRAL_CODES[code];
      if (pct) {
        setDiscountPct(pct);
        setReferralStatus('valid');
      } else {
        setDiscountPct(0);
        setReferralStatus('invalid');
      }
    }, 900);
  };
  const pay = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      subscribePackage(selected);
      setStep('success');
    }, 1600);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2190">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-2191" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]" data-id="element-2192">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between" data-id="element-2193">
          <div className="flex items-center gap-2" data-id="element-2194">
            {step === 'pay' && <button onClick={() => setStep('choose')} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg" data-id="element-2195">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-2196" />
              </button>}
            <div data-id="element-2197">
              <h2 className="text-lg font-display font-bold text-dark-navy" data-id="element-2198">
                {step === 'choose' && 'Choose your plan'}
                {step === 'pay' && 'Payment details'}
                {step === 'success' && 'Subscription active'}
              </h2>
              <p className="text-sm text-slate-500" data-id="element-2199">
                {step === 'choose' && 'Upgrade your Business Hub anytime.'}
                {step === 'pay' && `You're upgrading to the ${selected} plan.`}
                {step === 'success' && 'Thank you for upgrading.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-2200">
            <XIcon className="w-5 h-5" data-id="element-2201" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-2202">
          {step === 'choose' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2203">
              {paidPackages.map(p => {
            const isSel = selected === p.id;
            const isCurrent = currentPlanId === p.id;
            return <button key={p.id} onClick={() => setSelected(p.id)} className={`text-left rounded-2xl p-5 border-2 transition-colors relative flex flex-col ${isSel ? 'border-primary-indigo bg-indigo-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-2204">
                    {p.popular && <span className="absolute -top-2.5 left-5 px-2 py-0.5 bg-ai-purple text-white text-[10px] font-bold rounded-full uppercase tracking-wide" data-id="element-2205">
                        Popular
                      </span>}
                    {isSel && <CheckCircle2Icon className="w-5 h-5 text-primary-indigo absolute top-4 right-4" data-id="element-2206" />}
                    <div className="font-bold text-dark-navy mb-1" data-id="element-2207">
                      {p.name}
                    </div>
                    <div className="text-primary-indigo font-display font-bold text-2xl mb-1" data-id="element-2208">
                      AED {p.price.toLocaleString()}
                      <span className="text-sm text-slate-500 font-normal" data-id="element-2209">
                        /mo
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mb-4" data-id="element-2210">
                      {p.listingLimit === Infinity ? '100+ listings' : `${p.listingLimit} listings`}
                    </div>
                    <div className="space-y-1.5 mt-auto" data-id="element-2211">
                      {p.features.map(f => <div key={f} className="flex items-start gap-1.5 text-xs text-slate-600" data-id="element-2212">
                          <CheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2213" />{' '}
                          {f}
                        </div>)}
                    </div>
                    {isCurrent && <div className="mt-4 text-center text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-2214">
                        Current Plan
                      </div>}
                  </button>;
          })}
            </div>}

          {step === 'pay' && <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-2215">
              {/* Card form */}
              <div className="md:col-span-3 space-y-4" data-id="element-2216">
                <Field label="Cardholder name" placeholder="Fadi Al Mansoori" data-id="element-2217" />
                <div data-id="element-2218">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2219">
                    Card number
                  </label>
                  <div className="relative" data-id="element-2220">
                    <input placeholder="1234 5678 9012 3456" className="w-full pl-4 pr-11 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2221" />
                    <CreditCardIcon className="w-5 h-5 text-slate-300 absolute right-3 top-1/2 -translate-y-1/2" data-id="element-2222" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-2223">
                  <Field label="Expiry" placeholder="MM / YY" data-id="element-2224" />
                  <Field label="CVC" placeholder="123" data-id="element-2225" />
                </div>
                <Field label="Billing country" placeholder="United Arab Emirates" data-id="element-2226" />
              </div>

              {/* Summary */}
              <div className="md:col-span-2" data-id="element-2227">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5" data-id="element-2228">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2229">
                    Order Summary
                  </div>
                  <div className="flex items-center justify-between mb-2" data-id="element-2230">
                    <span className="text-sm text-slate-600" data-id="element-2231">
                      {plan.name} plan
                    </span>
                    <span className="font-bold text-dark-navy" data-id="element-2232">
                      AED {plan.price.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-3 text-sm text-slate-500" data-id="element-2233">
                    <span data-id="element-2234">Billed monthly</span>
                  </div>

                  {referralStatus === 'valid' && <div className="flex items-center justify-between mb-2 text-sm" data-id="element-2235">
                      <span className="text-green-600 font-medium" data-id="element-2236">
                        Referral ({discountPct}% off)
                      </span>
                      <span className="font-bold text-green-600" data-id="element-2237">
                        − AED {discountAmount.toLocaleString()}
                      </span>
                    </div>}

                  {/* Referral code */}
                  <div className="mb-3" data-id="element-2238">
                    <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-2239">
                      Referral code
                    </label>
                    <div className="flex gap-2" data-id="element-2240">
                      <div className="relative flex-1" data-id="element-2241">
                        <TagIcon className="w-4 h-4 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2242" />
                        <input value={referral} onChange={e => {
                      setReferral(e.target.value);
                      if (referralStatus !== 'idle') {
                        setReferralStatus('idle');
                        setDiscountPct(0);
                      }
                    }} onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyReferral();
                      }
                    }} placeholder="Enter code" className={`w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-sm uppercase placeholder:normal-case focus:ring-2 focus:ring-primary-indigo/20 ${referralStatus === 'valid' ? 'border-green-400' : referralStatus === 'invalid' ? 'border-red-400' : 'border-slate-200'}`} data-id="element-2243" />
                      </div>
                      <button onClick={applyReferral} disabled={!referral.trim() || referralStatus === 'checking'} className="px-3 py-2 text-sm font-medium text-primary-indigo border border-primary-indigo/30 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5" data-id="element-2244">
                        {referralStatus === 'checking' ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2245" /> : 'Apply'}
                      </button>
                    </div>
                    {referralStatus === 'valid' && <p className="flex items-center gap-1 text-xs text-green-600 mt-1.5" data-id="element-2246">
                        <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-2247" /> Code
                        applied — {discountPct}% off
                      </p>}
                    {referralStatus === 'invalid' && <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5" data-id="element-2248">
                        <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-2249" /> Invalid or
                        expired code
                      </p>}
                  </div>

                  <div className="h-px bg-slate-200 my-3" data-id="element-2250" />
                  <div className="flex items-center justify-between mb-4" data-id="element-2251">
                    <span className="font-bold text-dark-navy" data-id="element-2252">
                      Total today
                    </span>
                    <span className="font-display font-bold text-xl text-primary-indigo" data-id="element-2253">
                      AED {total.toLocaleString()}
                    </span>
                  </div>
                  <button onClick={pay} disabled={processing} className="w-full py-3 bg-primary-indigo text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70" data-id="element-2254">
                    {processing ? <>
                        <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2255" />{' '}
                        Processing…
                      </> : <>
                        <LockIcon className="w-4 h-4" data-id="element-2256" /> Pay AED{' '}
                        {total.toLocaleString()}
                      </>}
                  </button>
                  <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 mt-3" data-id="element-2257">
                    <LockIcon className="w-3 h-3" data-id="element-2258" /> Secured & encrypted
                  </p>
                </div>
              </div>
            </div>}

          {step === 'success' && <div className="text-center py-8" data-id="element-2259">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2260">
                <CheckCircle2Icon className="w-11 h-11 text-green-600" data-id="element-2261" />
              </motion.div>
              <h3 className="text-2xl font-display font-bold text-dark-navy mb-2" data-id="element-2262">
                Payment successful
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-2263">
                Your{' '}
                <span className="font-bold text-dark-navy" data-id="element-2264">{selected}</span>{' '}
                plan is now active. Enjoy the full Business Hub.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-xl mb-8" data-id="element-2265">
                <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-2266" />
                <span className="text-sm font-medium text-primary-indigo" data-id="element-2267">
                  {selected} Plan · Active subscription
                </span>
              </div>
              <div data-id="element-2268">
                <button onClick={onClose} className="px-8 py-3 bg-primary-indigo text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-md" data-id="element-2269">
                  Done
                </button>
              </div>
            </div>}
        </div>

        {/* Footer (choose step) */}
        {step === 'choose' && <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex justify-end gap-3" data-id="element-2270">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg" data-id="element-2271">
              Cancel
            </button>
            <button onClick={() => setStep('pay')} className="px-6 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700" data-id="element-2272">
              Continue to payment
            </button>
          </div>}
      </motion.div>
    </div>;
}
function Field({
  label,
  placeholder
}: {
  label: string;
  placeholder: string;
}) {
  return <div data-id="element-2273">
      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2274">
        {label}
      </label>
      <input placeholder={placeholder} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-2275" />
    </div>;
}